import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { Modal, Button, Group, Text, Progress, Badge, Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';
import TestsApi from "../../api/TestsApi";

/* Set Winner waits here until every variation has a screenshot of its latest published
   version (captured in the background after each Publish). Polls the readiness check and
   continues on its own once everything is ready. A failed or stuck capture can be retried,
   or the user can explicitly continue without it - an old screenshot is never used. */

const POLL_MS = 5000;

const STATE_LABEL: any = {
  ready: { label: 'Ready', color: 'green' },
  pending: { label: 'Preparing', color: 'blue' },
  missing: { label: 'Queued', color: 'blue' },
  stuck: { label: 'Delayed', color: 'orange' },
  failed: { label: 'Failed', color: 'red' },
};

export function ScreenshotReadinessModal({ opened, onClose, onReady, selectedMemberWebsite, pageId, initialStatus }: any) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<any>(initialStatus || null);
  const [retrying, setRetrying] = useState(false);
  const continuedRef = useRef(false);

  const handleResponse = (response: any) => {
    if (response.ok) {
      return response.json();
    }
    if (response.status === 401 || response.status === 400) {
      navigate("/logout");
    }
  };

  const applyStatus = (data: any) => {
    if (!data || data.status !== 1) {
      return;
    }
    setStatus(data.data);
    if (data.data.all_ready && !continuedRef.current) {
      continuedRef.current = true;
      onReady(false);
    }
  };

  const loadStatus = () => {
    TestsApi.getvariationscreenshotstatus({ system_website_id: selectedMemberWebsite, page_id: pageId })
      .then(handleResponse)
      .then(applyStatus)
      .catch((error) => console.error("API Error:", error));
  };

  useEffect(() => {
    if (!opened) {
      return;
    }
    continuedRef.current = false;
    if (initialStatus) {
      setStatus(initialStatus);
    }
    loadStatus();
    const timer = setInterval(loadStatus, POLL_MS);
    return () => clearInterval(timer);
  }, [opened]);

  const handleRetry = () => {
    setRetrying(true);
    TestsApi.retryvariationscreenshot({ system_website_id: selectedMemberWebsite, page_id: pageId })
      .then(handleResponse)
      .then((data) => {
        setRetrying(false);
        if (data && data.status === 1) {
          setStatus(data.data);
        } else if (data) {
          notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: data.message });
        }
      })
      .catch((error) => {
        setRetrying(false);
        console.error("API Error:", error);
      });
  };

  const total = status ? status.total : 0;
  const ready = status ? status.ready : 0;
  const variations = status ? status.variations : [];
  const hasProblem = variations.some((v: any) => v.state === 'failed' || v.state === 'stuck');

  return (
    <Modal opened={opened} onClose={onClose} title={<Text weight={500}>PREPARING VARIATION SCREENSHOTS</Text>} size="lg" trapFocus={false}>
      <Text fz={14}>
        Variation screenshots are still being prepared. Please wait until they are ready before setting the winner.
        They are saved to Test History so you can see exactly what each variation looked like.
      </Text>
      <Group position="apart" mt={15} mb={5}>
        <Text fz={14} weight={500}>{ready} of {total} variation screenshots ready</Text>
        {!hasProblem && ready < total && <Loader size="xs" />}
      </Group>
      <Progress value={total > 0 ? (ready / total) * 100 : 0} />
      {variations.map((variation: any) => {
        const state = STATE_LABEL[variation.state] || STATE_LABEL.pending;
        return (
          <div key={variation.variation_no} style={{ marginTop: 10 }}>
            <Group position="apart" noWrap>
              <Text fz={14}>{variation.name}{variation.is_control === 1 && !/control/i.test(variation.name) ? ' (Control)' : ''}</Text>
              <Badge color={state.color} variant="light">{state.label}</Badge>
            </Group>
            {(variation.state === 'failed' || variation.state === 'stuck') && variation.error && (
              <Text fz={12} color="red">{variation.error}{variation.attempts > 0 ? ` (after ${variation.attempts} attempt${variation.attempts > 1 ? 's' : ''})` : ''}</Text>
            )}
          </div>
        );
      })}
      {hasProblem && (
        <Text fz={13} mt={15} color="dimmed">
          You can retry the screenshots, or set the winner without them. Variations without a screenshot
          will show "Screenshot not captured" in Test History.
        </Text>
      )}
      <Group position="right" mt={20}>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        {hasProblem && (
          <Button variant="default" loading={retrying} onClick={handleRetry}>Retry Screenshots</Button>
        )}
        {status && status.can_skip && (
          <Button color="red" onClick={() => { continuedRef.current = true; onReady(true); }}>
            Set Winner Without These Screenshots
          </Button>
        )}
      </Group>
    </Modal>
  );
}
